import { useState, useEffect } from "react";
import { Lock, User, Envelope, Eye, EyeSlash, ArrowRight, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_collab-space-83/artifacts/cz1qezzm_LOGO%201%20PNG%20FILE%20%281%29.png";

export default function JoinPage({ code, onJoined }) {
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/auth/invite/${code}`);
        setInvite(res.data);
        if (res.data.email) setEmail(res.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invite link");
      }
    };
    validate();
  }, [code]);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/join`, {
        code, name: name.trim(), email: email.trim().toLowerCase(), password,
      }, { withCredentials: true });
      toast.success("Account created! Welcome to SkyeAI.");
      onJoined(res.data.user, res.data.session_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    }
    setLoading(false);
  };

  if (error) {
    return (
      <div className="login-scene">
        <div className="login-orbs" aria-hidden="true">
          <div className="login-orb login-orb-1" />
          <div className="login-orb login-orb-2" />
          <div className="login-orb login-orb-3" />
        </div>
        <div className="login-noise" aria-hidden="true" />
        <div className="w-full max-w-sm mx-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 bg-[#ef4444]/10">
            <LinkIcon size={24} weight="bold" className="text-[#ef4444]" />
          </div>
          <h2 className="text-lg font-bold text-white mb-2">Invite Link Invalid</h2>
          <p className="text-sm text-[#71717A]">{error}</p>
          <a href="/" className="inline-block mt-4 text-sm text-[#9D32F3] hover:underline">Go to login</a>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="login-scene">
        <div className="login-orbs" aria-hidden="true">
          <div className="login-orb login-orb-1" />
          <div className="login-orb login-orb-2" />
          <div className="login-orb login-orb-3" />
        </div>
        <div className="login-noise" aria-hidden="true" />
        <div className="relative z-10">
          <p className="text-sm text-[#71717A]">Validating invite...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-scene">
      {/* Animated mesh gradient orbs */}
      <div className="login-orbs" aria-hidden="true">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      {/* Noise overlay */}
      <div className="login-noise" aria-hidden="true" />

      <div className="w-full max-w-sm mx-4 relative z-10">
        {/* Flaming head mascot */}
        <div className="text-center mb-8">
          <div className="login-fire-container">
            <div className="login-embers" aria-hidden="true">
              {[...Array(20)].map((_, i) => (
                <div key={i} className={`login-ember login-ember-${i}`} />
              ))}
            </div>
            <div className="login-flames" aria-hidden="true">
              {[...Array(18)].map((_, i) => (
                <div key={i} className={`login-flame login-flame-${i}`} />
              ))}
            </div>
            <div className="login-heat-haze" aria-hidden="true" />
            <div className="login-fire-glow" />
            <div className="login-head-circle login-float">
              <img src="/skyeai_head.png" alt="" className="login-head-img" draggable={false} />
            </div>
          </div>

          <img src={LOGO_URL} alt="SkyeAI" className="login-brand-logo login-title" draggable={false} />
          <p className="login-subtitle text-sm text-[#71717A] mt-2 h-5">Join the team</p>
        </div>

        {/* Glass card */}
        <form
          onSubmit={handleJoin}
          className={`login-glass-card rounded-2xl p-6 space-y-5 transition-all duration-500 ${focused ? "login-card-glow" : ""}`}
          data-testid="join-form"
        >
          <div>
            <label className="block text-[10px] font-bold text-[#A1A1AA]/70 mb-1.5 uppercase tracking-[0.2em]">Full Name</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                className="login-input w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none transition-all"
                data-testid="join-name" autoFocus
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#A1A1AA]/70 mb-1.5 uppercase tracking-[0.2em]">Email</label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
                className="login-input w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none transition-all disabled:opacity-60"
                data-testid="join-email" disabled={!!invite.email}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#A1A1AA]/70 mb-1.5 uppercase tracking-[0.2em]">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters"
                className="login-input w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none transition-all"
                data-testid="join-password"
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A93] hover:text-[#A1A1AA] transition-all" tabIndex={-1}>
                {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !name.trim() || !email.trim() || password.length < 6} data-testid="join-submit"
            className="login-submit-btn login-shimmer-btn w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white rounded-xl transition-all duration-300 disabled:opacity-30 overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}>
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="login-spinner" />
                Creating account...
              </span>
            ) : (
              <><span>Create Account</span><ArrowRight size={14} weight="bold" className="login-arrow" /></>
            )}
          </button>
        </form>

        <p className="login-footer text-center text-[10px] text-[#52525B] mt-8 tracking-[0.15em] uppercase">SkyeAI Command Center</p>
      </div>
    </div>
  );
}
